import React, { useEffect, useRef, useState } from 'react';
import { useProjectsList } from '../../../../contexts/ProjectsContext';

const ManagementDashboardFilters = ({
  onFiltersChange,
  onLoadingChange,
  initialProjectID = null,
  initialMonth = null,
  includeAllProjectsOption = false
}) => {
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [projectID, setProjectID] = useState(() => {
    if (initialProjectID !== null && initialProjectID !== undefined && String(initialProjectID) !== '') {
      return String(initialProjectID);
    }
    return '';
  });
  const [month, setMonth] = useState('');
  const prevFiltersRef = useRef({ projectID: null, month: null });

  const hasNoProjects = !isProjectsLoading && (!projects || projects.length === 0);
  const builderLevelOption = { id: '', value: '', label: 'Builder Level' };
  const allProjectsOption = { id: '', value: '', label: 'All Projects' };

  const projectOptions = hasNoProjects
    ? [includeAllProjectsOption ? allProjectsOption : builderLevelOption]
    : includeAllProjectsOption
      ? [allProjectsOption, ...(projectsForDropdown || [])]
      : projectsForDropdown || [];

  const hasValidInitialProject =
    initialProjectID !== null &&
    initialProjectID !== undefined &&
    String(initialProjectID) !== '';

  useEffect(() => {
    if (initialMonth) {
      setMonth(Number(initialMonth));
      return;
    }
    setMonth(new Date().getMonth() + 1);
  }, [initialMonth]);

  // Sync from parent only when initialProjectID changes (navigation / external updates).
  // Do NOT depend on local projectID — that caused revert loops when the user changed project.
  useEffect(() => {
    if (!hasValidInitialProject) return;
    setProjectID(String(initialProjectID));
  }, [initialProjectID, hasValidInitialProject]);

  useEffect(() => {
    if (isProjectsLoading) return;

    if (!projects?.length) {
      setProjectID('');
      return;
    }

    if (hasValidInitialProject || includeAllProjectsOption) return;

    const firstProjectValue =
      projectsForDropdown?.[0]?.value !== undefined
        ? projectsForDropdown[0].value
        : projects[0]?.id;

    if (
      firstProjectValue !== undefined &&
      firstProjectValue !== null &&
      projectID === ''
    ) {
      setProjectID(String(firstProjectValue));
    }
  }, [
    projects,
    projectsForDropdown,
    includeAllProjectsOption,
    isProjectsLoading,
    hasValidInitialProject,
    projectID
  ]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isProjectsLoading);
    }
  }, [isProjectsLoading, onLoadingChange]);

  useEffect(() => {
    if (
      onFiltersChange &&
      projectID !== undefined &&
      projectID !== null &&
      month &&
      (prevFiltersRef.current.projectID !== projectID || prevFiltersRef.current.month !== month)
    ) {
      prevFiltersRef.current = { projectID, month };
      onFiltersChange({ projectID, month });
    }
  }, [projectID, month, onFiltersChange]);

  return (
    <div className="w-full">
      <div className="flex items-center gap-4">
        <div className="w-full">
          <p className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase mb-1">
            Project
          </p>
          <div className="relative w-full">
            <select
              value={projectID ?? ''}
              onChange={(e) => setProjectID(e.target.value)}
              className="w-full h-10 rounded-xl border border-gray-300 bg-white px-4 pr-10 text-[#0f2a45] text-sm font-semibold outline-none appearance-none">
              {projectOptions.map((option) => (
                <option key={option.value || 'builder-level'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagementDashboardFilters;
